import { Person, Remove } from '@mui/icons-material';
import { Box, Chip, IconButton, Paper, Typography } from '@mui/material';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import React from 'react';

export type Executive = {
  id: string;
  name: string;
  email: string;
};

interface ExecutiveRowProps {
  executive: Executive;
  type: 'commercial' | 'collection';
  onDelete?: () => void;
  canDelete?: boolean;
}

const ExecutiveRow: React.FC<ExecutiveRowProps> = ({
  executive,
  type,
  onDelete,
  canDelete,
}) => {
  const hasValue = executive.name && executive.name.trim() !== '';
  const displayValue = hasValue ? executive.name : 'No hay información';
  const textToShow =
    hasValue && displayValue.length > 35
      ? displayValue.slice(0, 35) + '...'
      : displayValue;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        mb: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box
        sx={{
          backgroundColor: hasValue
            ? 'rgba(25, 118, 210, 0.1)'
            : 'rgba(158, 158, 158, 0.1)',
          borderRadius: '50%',
          p: 0.4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '24px',
          height: '24px',
          flexShrink: 0,
        }}
      >
        <Person
          sx={{
            fontSize: '0.8rem',
            color: hasValue ? 'primary.main' : 'text.disabled',
          }}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Chip
            label={type === 'commercial' ? 'Comercial' : 'Cobranza'}
            size="small"
            color={type === 'commercial' ? 'primary' : 'warning'}
            variant="outlined"
            sx={{ height: '16px', fontSize: '0.6rem', mb: 0.2 }}
          />
          {hasValue ? (
            <LightTooltip
              title={<Typography padding={1}>{executive.name}</Typography>}
            >
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}
              >
                {textToShow}
              </Typography>
            </LightTooltip>
          ) : (
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.75rem',
                lineHeight: 1.2,
                color: 'text.disabled',
                fontStyle: 'italic',
              }}
            >
              {displayValue}
            </Typography>
          )}
          {executive.email && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.65rem', display: 'block' }}
            >
              {executive.email}
            </Typography>
          )}
        </Box>
        {canDelete && onDelete && (
          <IconButton
            size="small"
            onClick={onDelete}
            sx={{
              padding: 0.75,
              color: 'error.main',
              flexShrink: 0,
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              ml: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.15)',
              },
            }}
          >
            <Remove sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        )}
      </Box>
    </Paper>
  );
};

export default ExecutiveRow;
